import { useMemo } from 'react'

import { SanityPrintConfiguratorStrings } from '@data/sanity/queries/types/strings'
import {
  SanityPrintAreaType,
  SanityPrintFeeCombination,
  SanityPrintPermission,
} from '@data/sanity/queries/types/print'
import { SanityShopPrint } from '@data/sanity/queries/types/shop'
import { SanitySimplePortableText } from '@data/sanity/queries/types/content'
import { SanityProductVariantFragment } from '@data/sanity/queries/types/product'
import { useGetFormattedPrice } from '@lib/helpers'
import {
  ImageWithAreas,
  SlotConfiguration,
  SlotWithConfiguration,
} from '@lib/print/types'

import Alert from '@components/alert'
import SimplePortableText from '@components/portable-text/simple'
import PrintConfiguratorSlotImageField from './image-field'
import PrintConfiguratorSlotPrintPreview from './print-preview'
import PrintConfiguratorSlotSubmitButton from './submit-button'
import PrintConfiguratorSlotTextField from './text-field'

export interface PrintConfiguratorSlotProps {
  slotsWithConfiguration: SlotWithConfiguration[]
  activeSlotIndex: number
  prints: SanityShopPrint[]
  printFeeCombinations: SanityPrintFeeCombination[]
  totalPrice: number
  permissions?: SanityPrintPermission | null
  imageWithAreas?: ImageWithAreas | null
  showAddToCartButton?: boolean
  printConfiguratorStrings: SanityPrintConfiguratorStrings
  addToCartError: SanitySimplePortableText | null
  productVariant: SanityProductVariantFragment
  onConfigurationChange: (
    slotId: string,
    newConfiguration: Partial<SlotConfiguration>,
  ) => void
  onNextClick: () => void
  onAddToCartClick: () => void
}

const PrintConfiguratorSlot = ({
  slotsWithConfiguration,
  activeSlotIndex,
  prints,
  printFeeCombinations,
  totalPrice,
  permissions,
  imageWithAreas,
  showAddToCartButton,
  printConfiguratorStrings,
  addToCartError,
  productVariant,
  onConfigurationChange,
  onNextClick,
  onAddToCartClick,
}: PrintConfiguratorSlotProps) => {
  const activeSlot = useMemo(() => {
    return slotsWithConfiguration[activeSlotIndex]
  }, [activeSlotIndex, slotsWithConfiguration])

  const activePrints = useMemo(() => {
    return prints.filter((print) => print.slot?._id === activeSlot?._id)
  }, [activeSlot, prints])

  const imageAreaTypes = useMemo(() => {
    return imageWithAreas?.areas?.map((area) => area.type) ?? []
  }, [imageWithAreas])

  const getFormattedPrice = useGetFormattedPrice()
  const totalPriceString = getFormattedPrice(totalPrice)

  const fields = useMemo(() => {
    const hasLogoPermission = !!permissions?.logo
    const hasImagePermission = !!permissions?.image
    const hasTextPermission = !!permissions?.text
    const hasLogoArea = imageAreaTypes.includes(SanityPrintAreaType.LOGO)
    const hasImageArea = imageAreaTypes.includes(SanityPrintAreaType.IMAGE)
    const hasTextArea = imageAreaTypes.includes(SanityPrintAreaType.TEXT)
    const hasLogoPrints = activePrints.some((print) => print.type === 'logo')
    const hasImagePrints = activePrints.some((print) => print.type === 'image')
    const showLogos = hasLogoPermission && hasLogoArea && hasLogoPrints
    const showImages = hasImagePermission && hasImageArea && hasImagePrints
    const showText = hasTextPermission && hasTextArea

    return [
      showText ? (
        <PrintConfiguratorSlotTextField
          printConfiguratorStrings={printConfiguratorStrings}
          slotsWithConfiguration={slotsWithConfiguration}
          printFeeCombinations={printFeeCombinations}
          activeSlotIndex={activeSlotIndex}
          imageAreaTypes={imageAreaTypes}
          permissions={permissions}
          onConfigurationChange={onConfigurationChange}
        />
      ) : null,
      showLogos || showImages ? (
        <PrintConfiguratorSlotImageField
          printConfiguratorStrings={printConfiguratorStrings}
          slotsWithConfiguration={slotsWithConfiguration}
          printFeeCombinations={printFeeCombinations}
          activeSlotIndex={activeSlotIndex}
          imageAreaTypes={imageAreaTypes}
          activePrints={activePrints}
          permissions={permissions}
          onConfigurationChange={onConfigurationChange}
        />
      ) : null,
    ].filter(Boolean) as JSX.Element[]
  }, [
    activePrints,
    activeSlotIndex,
    imageAreaTypes,
    onConfigurationChange,
    permissions,
    printConfiguratorStrings,
    printFeeCombinations,
    slotsWithConfiguration,
  ])

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 md:gap-10">
      <PrintConfiguratorSlotPrintPreview
        activeSlot={activeSlot}
        imageWithAreas={imageWithAreas}
      />

      <div className="flex flex-col gap-4">
        <div className="grow flex flex-col md:border md:border-gray-light bg-white">
          <div className="grow px-5 sm:px-8 py-8 space-y-6">
            <div className="space-y-3">
              <h3 className="leading-tight font-semibold text-[22px]">
                {activeSlot.name}
              </h3>
              {!!activeSlot.description && (
                <SimplePortableText
                  content={activeSlot.description}
                  className="rc rc-text-base"
                />
              )}
            </div>

            <div className="flex flex-col gap-y-8">
              {fields.map((field, index) => (
                <>
                  {index > 0 && <div className="border-b border-gray-light" />}
                  {field}
                </>
              ))}
            </div>
          </div>

          <div
            className="grow-0 flex justify-between items-center gap-3 py-3 px-5 sm:px-8"
            style={{
              boxShadow: '0px -4px 10px 0px rgba(0, 0, 0, 0.03)',
            }}
          >
            <span className="leading-tight font-semibold text-[22px]">
              {totalPriceString}
            </span>

            <PrintConfiguratorSlotSubmitButton
              productVariant={productVariant}
              activeSlot={activeSlot}
              imageAreaTypes={imageAreaTypes}
              activePrints={activePrints}
              permissions={permissions}
              showAddToCartButton={showAddToCartButton}
              onNextClick={onNextClick}
              onAddToCartClick={onAddToCartClick}
            />
          </div>
        </div>

        {!!addToCartError && (
          <Alert error>
            <SimplePortableText
              content={addToCartError}
              className="rc rc-error"
            />
          </Alert>
        )}
      </div>
    </div>
  )
}

export default PrintConfiguratorSlot
